import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Overline } from "../Reveal";
import { AnimatedDnaHelix } from "../Molecules";
import { NAV_LINKS } from "../../data";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (window.lenis) window.lenis.scrollTo(el, { duration: 1.4 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setForm({ name: "", email: "", org: "", message: "" });
    }, 3500);
  };

  const field =
    "w-full bg-white border border-black/15 rounded-lg px-4 py-3 font-body text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white pt-28 lg:pt-40 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* left */}
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Contact</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-zinc-900 mt-6 leading-tight">
                Let's build the future of medicine together.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-body text-base lg:text-lg leading-relaxed text-zinc-600 mt-7 max-w-md">
                Whether you're a researcher, partner or investor — we'd love to hear
                from you. Reach out and a member of our team will respond within two
                business days.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 space-y-4 font-mono text-sm text-zinc-600">
                <a
                  href="mailto:hello@biosoma.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-2 hover:text-brand transition-colors w-fit"
                >
                  hello@biosoma.com <ArrowUpRight size={14} />
                </a>
                <div>+1 (617) 555-0142</div>
                <div className="text-zinc-400">
                  238 Genome Way · Cambridge, MA 02142
                </div>
              </div>
            </Reveal>
          </div>

          {/* form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                className="grid sm:grid-cols-2 gap-5 bg-[#FAFAFA] border border-black/10 rounded-2xl p-6 lg:p-10"
              >
                <div className="sm:col-span-1">
                  <label className="font-mono text-[11px] tracking-widest uppercase text-zinc-500">
                    Name
                  </label>
                  <input
                    data-testid="contact-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    placeholder="Jane Doe"
                    className={`${field} mt-2`}
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="font-mono text-[11px] tracking-widest uppercase text-zinc-500">
                    Email
                  </label>
                  <input
                    data-testid="contact-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    placeholder="jane@company.com"
                    className={`${field} mt-2`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono text-[11px] tracking-widest uppercase text-zinc-500">
                    Organization
                  </label>
                  <input
                    data-testid="contact-org"
                    name="org"
                    value={form.org}
                    onChange={onChange}
                    placeholder="Company / Institution"
                    className={`${field} mt-2`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono text-[11px] tracking-widest uppercase text-zinc-500">
                    Message
                  </label>
                  <textarea
                    data-testid="contact-message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    rows={4}
                    placeholder="Tell us how we can help…"
                    className={`${field} mt-2 resize-none`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={sent}
                    className="inline-flex items-center gap-2 font-body text-sm font-semibold text-white bg-brand hover:bg-brand-hover disabled:bg-emerald-600 px-7 py-3.5 rounded-full transition-colors"
                  >
                    {sent ? (
                      <>
                        <Check size={16} /> Message sent
                      </>
                    ) : (
                      "Send message"
                    )}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>

      {/* footer */}
      <footer className="relative mt-28 lg:mt-40 border-t border-black/10 overflow-hidden">
        <div className="absolute -right-4 -bottom-10 opacity-[0.18] pointer-events-none hidden md:block">
          <AnimatedDnaHelix rungs={14} className="h-[300px]" style={{ width: 150 }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-12">
            <div>
              <button
                onClick={() => scrollTo("hero")}
                className="flex items-center gap-2.5"
                data-testid="footer-logo"
              >
                <span className="w-3 h-3 rounded-full bg-brand" />
                <span className="font-heading font-extrabold text-2xl tracking-tight text-zinc-900">
                  Bio<span className="text-brand">Soma</span>
                </span>
              </button>
              <p className="font-body text-sm text-zinc-500 mt-5 max-w-xs">
                Programmable genetic medicines for the diseases that matter most.
              </p>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  data-testid={`footer-nav-${l.href}`}
                  className="font-body text-sm text-zinc-600 hover:text-brand transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-16 pt-8 border-t border-black/5 font-mono text-[11px] tracking-wider uppercase text-zinc-400">
            <span>© 2026 BioSoma. All rights reserved.</span>
            <span>Cambridge · Basel · Singapore</span>
          </div>
        </div>
      </footer>
    </section>
  );
}
